import { IOption } from 'modules/core/types';
import { IVehicleModel } from 'modules/core/components/VehicleFilters/types';

import { ADD_VEHICLE_FORM_INITIAL_VALUES } from './consts/consts';
import { IAIVinDecodedData } from './types';

export const getCurrentVehicleModelsFilterOptions = (
  make: string | undefined,
  vehicleModels: IVehicleModel[],
): IOption[] => {
  if (!make) return [];

  return vehicleModels
    .filter((item) => make === Object.keys(item)[0])
    .reduce((vehicleModel: IOption[], item) => {
      Object.values(item)[0].forEach((model: Record<string, any>) =>
        vehicleModel.push({ title: model.name, value: model.name }),
      );

      return vehicleModel;
    }, []);
};

/**
 * Class is responsible for mapping AI-decoded VIN data.
 *
 * The following data are mapping:
 * make: exact or partial case-insensitive mapping
 * model: exact or partial case-insensitive mapping, map only if make was mapped
 * trim: value from response
 * year: exact number mapping
 * mileage: value from response
 */
export class AIResponseMapper {
  private makeOptions: string[];
  private yearOptions: number[];
  private models: IVehicleModel[];

  constructor(makeOptions: IOption[], yearOptions: IOption[], models: IVehicleModel[]) {
    this.models = models;
    this.makeOptions = makeOptions.map((option) => option.value as string) || [];
    this.yearOptions = yearOptions.map((option) => option.value as number) || [];
  }

  mapFields(data: IAIVinDecodedData) {
    let resultValues: any = {
      make: ADD_VEHICLE_FORM_INITIAL_VALUES.make,
      model: ADD_VEHICLE_FORM_INITIAL_VALUES.model,
      trim: ADD_VEHICLE_FORM_INITIAL_VALUES.trim,
      year: ADD_VEHICLE_FORM_INITIAL_VALUES.year,
      mileage: ADD_VEHICLE_FORM_INITIAL_VALUES.mileage,
    };

    if (!data) return resultValues;

    const { make, model, trim, year, mileage } = data;
    const makeMatch = this.findBestMatch(this.makeOptions, make);
    const yearMatch = this.findExactMatch(this.yearOptions, year);
    const currentModels = makeMatch ? getCurrentVehicleModelsFilterOptions(makeMatch, this.models) : [];
    const modelOptions = currentModels.map((option: IOption) => option.value) as string[];
    const modelMatch = this.findBestMatch(modelOptions, model);

    resultValues = {
      ...resultValues,
      ...(makeMatch && { make: { title: makeMatch, value: makeMatch } }),
      ...(makeMatch && modelMatch && { model: { title: modelMatch, value: modelMatch } }),
      ...(trim && { trim: trim }),
      ...(yearMatch && { year: { title: String(yearMatch), value: yearMatch } }),
      ...(Number.isFinite(mileage) && mileage >= 0 && { mileage: mileage }),
    };

    return resultValues;
  }

  private findBestMatch(options: string[], searchValue: string): string | null {
    if (!Array.isArray(options) || !options.length || !searchValue) return null;

    const exactMatch = this.findExactMatch(options, searchValue);

    if (exactMatch) return exactMatch;

    let bestMatch = null;
    let bestLength = 0;

    options.forEach((option) => {
      const commonLength = this.longestCommonWordSeqLength(searchValue, option);

      if (commonLength > bestLength) {
        bestLength = commonLength;
        bestMatch = option;
      }
    });

    return bestMatch;
  }

  private findExactMatch<T extends string | number>(options: T[], searchValue: T): T | null {
    if (!Array.isArray(options) || !options.length || !searchValue) return null;

    const normalizeValue = (value: T) => (typeof value === 'string' ? value.toLowerCase() : value);

    return options.find((option) => normalizeValue(option) === normalizeValue(searchValue)) || null;
  }

  private longestCommonWordSeqLength(source: string, target: string): number {
    const sourceWords = source.toLowerCase().split(/\s+/);
    const targetWords = target.toLowerCase().split(/\s+/);
    const matchMatrix = Array(sourceWords.length + 1)
      .fill(0)
      .map(() => Array(targetWords.length + 1).fill(0));

    let maxLength = 0;

    for (let i = 1; i <= sourceWords.length; i++) {
      for (let j = 1; j <= targetWords.length; j++) {
        if (sourceWords[i - 1] === targetWords[j - 1]) {
          matchMatrix[i][j] = matchMatrix[i - 1][j - 1] + 1;

          if (matchMatrix[i][j] > maxLength) maxLength = matchMatrix[i][j];
        }
      }
    }

    return maxLength;
  }
}
